import React, { useEffect, useState } from "react";
import TopNavbar from "../../shares/TopNavbar";
import * as api from "../../services/api";
import * as Account from "../../account";

const FORA_LOGIN_URL = "https://gofora.net/Account/Login/1";

export default function Callback() {
    const query = new URLSearchParams(window.location.search);
    const [status, setStatus] = useState("running");
    const [message, setMessage] = useState("Completing QuickBooks connection...");

    useEffect(() => {
        const queryData = {
            pendingId: query.get("pending")
        };
        const hasQueryData = Boolean(queryData.pendingId);
        const isResume = query.get("resume") === "1";
        const callbackError = query.get("error");

        if (callbackError === "missing_callback") {
            setStatus("error");
            setMessage("Missing QuickBooks callback data. Start again from Connect to QuickBooks.");
            return;
        }

        if (callbackError === "invalid_state") {
            setStatus("error");
            setMessage("QuickBooks callback validation failed. Start again from Connect to QuickBooks.");
            return;
        }

        const readPendingData = () => {
            try {
                const raw = window.localStorage.getItem(Account.quickBooksCallbackPendingKey);
                if (!raw) return null;
                const parsed = JSON.parse(raw);
                if (!parsed?.pendingId) return null;
                return parsed;
            } catch {
                return null;
            }
        };

        const clearPendingData = () => {
            window.localStorage.removeItem(Account.quickBooksCallbackPendingKey);
        };

        const savePendingData = (payload) => {
            window.localStorage.setItem(Account.quickBooksCallbackPendingKey, JSON.stringify(payload));
        };

        const data = hasQueryData ? queryData : (isResume ? readPendingData() : null);
        if (!data) {
            setStatus("error");
            setMessage("Missing QuickBooks callback data. Start again from Connect to QuickBooks.");
            return;
        }

        api.settingQuickbook(data)
            .then(response => {
                if (response) {
                    clearPendingData();
                    window.location.href = "/?platform=quickbooks_online";
                    return;
                }
                setStatus("error");
                setMessage("QuickBooks authorization finished, but the connection could not be saved.");
            })
            .catch(err => {
                console.log(err);
                const account = Account.getUserAccount();
                if (!account) {
                    savePendingData(data);
                    window.location.href = FORA_LOGIN_URL;
                    return;
                }
                if (err?.statusCode === 401) {
                    clearPendingData();
                    setStatus("error");
                    setMessage("Could not complete QuickBooks connection because your sign-in session is invalid. Please sign out, sign in again, and reconnect.");
                    return;
                }

                const serverMessage =
                    err?.data?.message ||
                    err?.data?.title ||
                    (typeof err?.data === "string" ? err.data : null);

                setStatus("error");
                setMessage(serverMessage || "Could not complete QuickBooks connection. Please try again.");
            });
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
            <TopNavbar />
            <main className="container mx-auto px-4 py-12 sm:py-16">
                <div className="mx-auto max-w-2xl rounded-2xl bg-white p-8 sm:p-10 shadow-lg shadow-slate-200/70 ring-1 ring-slate-200/80">
                    <p className="text-xs font-semibold tracking-wide text-slate-500">QuickBooks</p>
                    <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                        Connection callback
                    </h1>

                    <div
                        className={`mt-6 rounded-lg border px-4 py-3 text-sm leading-relaxed ${
                            status === "error"
                                ? "border-red-200 bg-red-50 text-red-700"
                                : "border-blue-200 bg-blue-50 text-blue-700"
                        }`}
                    >
                        {status !== "error" ? (
                            <div className="flex items-center gap-3">
                                <svg
                                    className="h-5 w-5 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    aria-hidden
                                >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                <span>{message}</span>
                            </div>
                        ) : (
                            <span>{message}</span>
                        )}
                    </div>

                    {status === "error" ? (
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <a
                                href="/connect"
                                className="inline-flex justify-center rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
                            >
                                Try connect again
                            </a>
                            <a
                                href="/"
                                className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                            >
                                Back to dashboard
                            </a>
                        </div>
                    ) : (
                        <p className="mt-6 text-sm text-slate-600">
                            Please wait while we finish the authorization process.
                        </p>
                    )}
                </div>
            </main>
        </div>
    );
}
